import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SessionService } from '@app/providers/services/session.service';
import { User } from '@app/models';
import { Observable, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ManifoldService } from '@app/providers/services/manifold.service';
import { CrawlerHealth } from '@app/modules/manifold/crawler-health';

@Component({
  selector: 'app-private',
  templateUrl: './private.component.html',
  styleUrls: ['./private.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PrivateComponent implements OnInit, OnDestroy {
  user: Observable<User>;
  isSidenavOpen = true;
  // On mobile the nav overlays the content (over) and starts closed; on desktop
  // it stays docked (side) and open. Driven by the breakpoint watcher below.
  sidenavMode: 'side' | 'over' = 'side';
  isMobile = false;
  // Shown while the router is mid-navigation (lazy-module download + route
  // resolvers), so a slow backend call doesn't make the app look frozen.
  navLoading = false;
  manifoldStatus: Exclude<CrawlerHealth, 'checking'> | null = null;
  /** Data Tables lives at /discovery, which is also the prefix of the Enrichment pages. */
  dataTablesActive = false;
  private sub = new Subscription();

  // The crawler-health badge is always visible in the nav, so it must not go
  // stale — re-check it on a timer and whenever the tab is brought back to the
  // foreground. Guarded by the wall-clock check so background timer throttling
  // can't drift the schedule and rapid tab-switching can't hammer the endpoint.
  private readonly STATUS_REFRESH_INTERVAL_MS = 300_000; // 5 minutes
  private statusInterval: any;
  private lastStatusRefreshAt = 0;
  private onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && Date.now() - this.lastStatusRefreshAt >= this.STATUS_REFRESH_INTERVAL_MS) {
      this.refreshManifoldStatus();
    }
  };

  constructor(
    private session: SessionService,
    private router: Router,
    private manifoldService: ManifoldService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit() {
    this.user = this.session.admin;
    this.dataTablesActive = isDataTablesUrl(this.router.url);

    // Collapse the side nav into an overlay drawer on mobile so it stops
    // squeezing the content column; keep it docked & open on larger screens.
    this.sub.add(
      this.breakpointObserver.observe('(max-width: 768px)').subscribe(({ matches }) => {
        this.isMobile = matches;
        this.sidenavMode = matches ? 'over' : 'side';
        this.isSidenavOpen = !matches;
      })
    );

    // Drive the navigation progress bar and auto-close the mobile drawer.
    // NavigationStart fires as soon as a link is clicked — before the lazy
    // module chunk downloads and before route resolvers run — so the bar covers
    // the whole "clicked but nothing visible yet" gap that felt like a freeze.
    this.sub.add(
      this.router.events.subscribe((e) => {
        if (e instanceof NavigationStart) {
          this.navLoading = true;
        } else if (e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError) {
          this.navLoading = false;
          if (e instanceof NavigationEnd) {
            this.dataTablesActive = isDataTablesUrl(e.urlAfterRedirects);
          }
          if (e instanceof NavigationEnd && this.isMobile) {
            this.isSidenavOpen = false;
          }
        }
      })
    );

    // Badge = the shared crawler-health verdict (same one the /manifold banner shows). 'checking' = no data yet.
    this.sub.add(
      this.manifoldService.crawlerHealth$.subscribe((health) => {
        this.manifoldStatus = health === 'checking' ? null : health;
      })
    );

    // Pause state is part of the verdict; load it, then fetch crawler statuses for the first verdict.
    this.sub.add(
      this.manifoldService
        .getQueuePausedState()
        .pipe(catchError(() => of(null)))
        .subscribe(() => this.refreshManifoldStatus())
    );

    this.statusInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.refreshManifoldStatus();
      }
    }, this.STATUS_REFRESH_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private refreshManifoldStatus() {
    this.lastStatusRefreshAt = Date.now();
    // The verdict itself comes from ManifoldService.crawlerHealth$ (subscribed in ngOnInit); this only triggers a fetch.
    this.manifoldService.getCrawlerStatus().subscribe({ error: () => undefined });
  }

  toggleSidenav() {
    this.isSidenavOpen = !this.isSidenavOpen;
  }

  logout() {
    this.session.logout();
    this.router.navigate(['/auth']);
  }

  get boxRouteActive(): boolean {
    return this.router.isActive('/boxes', true);
  }

  get boxCloneRouteActive(): boolean {
    return this.router.isActive('/boxes/clone', true);
  }
}

/** The Discovery data tables: /discovery and its pages, except the Enrichment section. */
export function isDataTablesUrl(url: string): boolean {
  const path = (url || '').split(/[?#]/)[0];
  return (path === '/discovery' || path.startsWith('/discovery/')) && !path.startsWith('/discovery/enrichment');
}
